<template>
    <div>
        <!--Create or Update Bookmark-->
        <v-dialog
            persistent
            v-model="modal"
            max-width="700">
            <v-card>
                <v-card-title
                    class="primary white--text"
                    :class="{
                        'headline': !isMobilePort,
                        'caption mobile-title': isMobilePort
                    }">
                    <span v-if="isUpdate">Edit Note / Bookmark</span>
                    <span v-else>Add Note / Bookmark</span>
                    <v-spacer></v-spacer>
                    <v-icon class="primary white--text" @click="closeModal">close</v-icon>
                </v-card-title>
                <v-card-text class="inner-modal-content">
                    <v-row>
                        <v-col class="font-weight-bold pb-0">
                            Note (optional)
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>

                            <v-alert
                                    :value="selectionTruncated"
                                    color="error"
                                    icon="warning"
                                    outlined
                            >
                                The note you are creating will only apply to the active section and although highlighted
                                text may expand outside of this section the highlight will only apply to text within
                                that section once the note is saved.
                            </v-alert>

                            <vue-editor v-model="editorData" :editor-toolbar="customToolbar"/>
                        </v-col>
                    </v-row>
                    <v-row align="center">
                        <template v-if="!loadingTags">
                            <v-col>
                                <v-autocomplete
                                    v-model="selectedTag"
                                    :items="tags"
                                    item-text="tag"
                                    item-value="id"
                                    chips
                                    small-chips
                                    dense
                                    outlined
                                    label="Project Tag"
                                    :rules="[v => !!v || 'Project tag is required']"
                                    hide-details="auto"
                                    ref="projectTag">
                                </v-autocomplete>
                            </v-col>
                            <v-col sm="auto">
                                <v-col cols="auto">
                                    <v-btn
                                        :disabled="selectedTag === 1"
                                        outlined
                                        @click="createNewTagModal(true, selectedTag)"
                                    >
                                        Edit Tag
                                    </v-btn>
                                    <v-btn
                                        outlined
                                        @click="createNewTagModal(false, selectedTag)"
                                    >
                                        Add Tag
                                    </v-btn>
                                </v-col>
                            </v-col>
                        </template>
                        <template v-else>
                            <v-col>
                                <v-progress-linear :indeterminate="true"></v-progress-linear>
                            </v-col>
                        </template>
                    </v-row>

                    <v-row v-if="activeEnterpriseProfile">
                        <v-col>
                            <v-select
                                v-model="selectedVisibility"
                                :items="visibilityItems"
                                label="Visibility"
                                outlined
                                flat
                                hide-details
                                dense
                            >
                                <template v-slot:selection="{ item }">
                                    <v-icon class="mr-2">{{ item.icon }}</v-icon>
                                    {{ item.text }}
                                </template>

                                <template v-slot:item="{ item }">
                                    <v-icon class="mr-2">{{ item.icon }}</v-icon>
                                    {{ item.text }}
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>

                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeModal">Cancel</v-btn>
                    <v-btn color="primary" :loading="isSaving" @click="saveNote">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--Remove Highlight annotation or bookmark Modal-->
        <v-dialog v-model="removeModal" max-width="400">
            <v-card>
                <v-card-title
                    class="primary white--text"
                    :class="{
                        'headline': !isMobilePort,
                        'caption mobile-title': isMobilePort
                    }">
                    Delete Note / Bookmark
                    <v-spacer></v-spacer>
                    <v-icon class="primary white--text" @click="removeModal = false">close</v-icon>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col class="text-center">
                            <p v-if="isEnterpriseAdminAction()" class="mb-0 mt-5">
                                Are you sure you want to delete this public note from {{getNoteCreatorName()}}? It will be removed for everyone in your Enterprise.
                            </p>
                            <p v-else class="mb-0 mt-5">
                                Are you sure you want to remove this note?
                            </p>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="removeModal = false">Cancel</v-btn>
                    <v-btn color="error" :loading="isDeleting" @click="deleteNote">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--Validate URLs in the user note -->
        <v-dialog v-model="invalidNoteUrl" max-width="400">
            <v-card>
                <v-card-title class="headline">Invalid URL</v-card-title>
                <v-card-text>
                    Your note contains an Invalid URL?
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" flat @click="invalidNoteUrl = false">Ok</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import TagsMixins from './mixins/tags';
    import urlHelper from './mixins/urlHelper';
    import ContentUtils from '../inc/contentUtils';
    import EditorConfigs from '../../inc/editorConfigBar';
    import Mobile from '../../inc/mixins/mobile';
    import { EventNames } from '../../../classes/EventNames';

    export default {
        data() {
            return {
                modal: false,
                removeModal: false,
                note: {},
                tagId: 0,
                editorData: '',
                isUpdate: false,
                invalidNoteUrl: false,
                selectionTruncated: false,
                isSaving: false,
                isDeleting: false,
                // Visibility
                selectedVisibility: 'private',
                visibilityItems: [
                    {value: 'private', text: 'Private (visible to just you)', icon: 'mdi-eye-off-outline'},
                    {value: 'public', text: 'Public (visible to everyone in your Enterprise)', icon: 'mdi-eye-outline'}
                ]
            }
        },
        mixins: [TagsMixins, urlHelper, ContentUtils, EditorConfigs, Mobile],
        computed: {
            currentUserId() {
                return parseInt(this.$store?.getters?.getUserId);
            }
        },
        methods: {
            saveNote() {
                // Validate URLs in the user note
                if(!this.validateLinksInNote(this.editorData)) {
                    this.invalidNoteUrl = true;
                    return false;
                }

                const isValid = this.$refs.projectTag.validate();
                if (!isValid) {
                    return;
                }

                let object = _.assign(this.note, {
                    'note': this.editorData,
                    'tag': this.selectedTag,
                    'visibility': this.selectedVisibility
                });

                this.isSaving = true;
                this.$http.post(Routing.generate('create_note'), object)
                    .then(response => {
                        // Set the note visibility
                        if(response?.data[0]?.is_public) {
                            this.selectedVisibility = 'public';
                        } else {
                            this.selectedVisibility = 'private';
                        }

                        // Set our class applier information.
                        let tagId = _.toInteger(object.tag);
                        let options,
                            array = [],
                            tagDefaultColor = '8fb08c',
                            tag = _.find(this.$store.getters.getTags, {'id': tagId});

                            let tagColor = (_.isUndefined(tag.color)) ? tagDefaultColor : tag.color;
                            if(undefined != tag) {
                                let currentTagElements = document.querySelectorAll("span[data-tag-id='" + tagId + "']");
                                if(currentTagElements) {
                                    this.updateTagHighlightColor(currentTagElements, tagColor);
                                }
                            }

                        let data = {
                            annotationId: response.data[0].annotation.id,
                            tagColor: tagColor,
                            tagId: tagId
                        };
                        let applier = this.applierUtil(data),
                            section = document.getElementById('action-wrapper-' + response.data[0].section_number),
                            id = response.data[0].section_number;
                        if (section) {
                            // Check to see if we have a tag change, if so remove old highlight.
                            if (this.tagId !== 0 && this.tagId !== tagId)
                                this.removeHighlight({
                                    sectionId: response.data[0].section_number,
                                    note: response.data[0],
                                    tag: {
                                        id: this.tagId,
                                        color: this.getTagColor(this.tagId)
                                    }
                                });
                            // Apply highlight to dom.
                            this.highlightUtil(applier, section, response.data[0].annotation, 'add');
                        }
                        if (this.isUpdate) {
                            options = {
                                action: 'update',
                                sectionId: id,
                                type: 'annotation',
                                id: response.data[0].id,
                                notes: {
                                    annotations: response.data
                                }
                            };
                            response.data[0].isUpdate = true;
                            EventBus.fire('update-note-preview-'+response.data[0].annotation.id, response.data[0]);
                        } else {
                            options = {
                                action: 'create',
                                sectionId: id,
                                notes: {
                                    annotations: response.data
                                }
                            };
                            // Add our note preview.
                            let _this = this;
                            let element = document.getElementById('note-annotation-'+response.data[0].annotation.id);
                            if (element) {
                                element.addEventListener('mouseenter', function(event) {
                                    EventBus.fire(
                                        'toggle-note-preview-'+_this.stripIds(event.target.id),
                                        {
                                            mouse: true,
                                            modal: true,
                                            event: event
                                        }
                                    );
                                });
                                element.addEventListener('mouseleave', function(event) {
                                    EventBus.fire(
                                        'toggle-note-preview-'+_this.stripIds(event.target.id),
                                        {
                                            mouse: false,
                                            modal: false
                                        }
                                    );
                                });
                            }
                        }
                        EventBus.fire('update-notes', options);
                        this.highlightedText = {};
                        if (window.getSelection) {
                            window.getSelection().removeAllRanges();
                        } else if (document.selection){
                            document.selection.empty();
                            let noteElement = document.getElementById(id+'_note');
                            (noteElement) ? noteElement.classList.add('v-icon--disabled') : '';
                        }
                        this.closeModal();
                        //if enterprise admin making the note private then hide it immediately from the list
                        if (this.hideableNote(response.data[0]) && this.selectedVisibility === 'private') {
                            this.hidePrivateNote(response.data[0].id, id);
                        }
                    })
                    .finally(()=>{
                        this.isSaving = false;
                    })
            },

            updateNoteData(note) {
                this.isUpdate = true;
                this.$http.get(Routing.generate('get_note', {id: note.id}))
                    .then(response => {
                        this.note = {
                            id: response.data.id,
                            annotation: response.data.annotation,
                        };
                        this.getUserTags(response.data.tag);
                        this.tagId = _.toInteger(response.data.tag);
                        this.editorData = response.data.annotation.text;

                        // Set the note visibility
                        if(response?.data?.is_public) {
                            this.selectedVisibility = 'public';
                        } else {
                            this.selectedVisibility = 'private';
                        }
                    });
            },

            updateTagHighlightColor(elements, tagColor) {
                _.forEach(elements, function(spanTag) {
                    let element = document.getElementById(spanTag.getAttribute('id'));
                    if(element) {
                        element.style.backgroundColor = "#"+tagColor;
                    }
                });
            },

            closeModal() {
                this.note = {};
                this.editorData = '';
                this.tags = [];
                this.selectedTag = [];
                this.loadingTags = true;
                this.modal = false;
            },

            deleteNote() {
                // Remove highlight.
                this.isDeleting = true;
                this.$http.get(Routing.generate('remove_note', {noteId: this.note.id}))
                    .then(response => {
                        try {
                            let data = {
                                sectionId: this.note.sectionID,
                                note: this.note,
                                tag: {
                                    id: this.note.tagID,
                                    color: this.getTagColor(this.note.tagID)
                                }
                            };
                            this.removeHighlight(data);
                        }catch(e) {
                            //should return nothing
                        }
                        this.removeModal = false;
                        let options = {
                            action: 'remove',
                            type: 'annotation',
                            id: this.note.id,
                            sectionId: this.note.sectionID
                        };
                        EventBus.fire('update-notes', options);
                    })
                    .finally(()=>{
                        this.isDeleting = false;
                    })
            },
            isEnterpriseNote() {
                return Boolean(this.note?.enterprise);
            },
            getNoteCreatorName() {
                return this.note?.userName;
            },
            getNoteCreatorId() {
                if(this.note.user && this.note.user.id) {
                    return parseInt(this.note.user.id);
                } else if(undefined !== this.note?.user) {
                    return parseInt(this.note.user);
                }
                return 0;
            },
            isEnterpriseAdminAction() {
                return this.isEnterpriseNote() &&
                    this.currentUserId !== this.getNoteCreatorId();
            },
            onNoteModal(note) {
                if (note.id) {
                    this.updateNoteData(note);
                } else {
                    this.selectedVisibility = this.activeEnterpriseProfile ? 'public' : 'private';
                    this.note = note;
                    this.getUserTags(1);
                    this.isUpdate = false;
                }
                this.modal = true;
                this.selectionTruncated = !!_.get(note, 'highlightedText.selectionTruncatedToSingleSection');
            },
            onNoteDelete(data) {
                this.note = data;
                this.removeModal = true;
            },
            onTagUpdated(data){
                this.selectedTag = data
            },
            hidePrivateNote(noteId, sectionId) {
                let options = {
                    action: 'remove',
                    type: 'annotation',
                    id: noteId,
                    sectionId: sectionId
                };
                EventBus.fire('update-notes', options);
            },
            hideableNote(noteObj){
                return noteObj?.enterprise && this.isEnterpriseAdmin
                    && this.currentUserId !== parseInt(noteObj.user.id);
            }
        },
        created() {
            EventBus.listen(EventNames.NOTE_MODAL, this.onNoteModal);
            EventBus.listen(EventNames.REMOVE_NOTE, this.onNoteDelete);
            EventBus.listen(EventNames.UPDATE_NOTE_TAGS, this.updateUserTags);
        },
        beforeDestroy() {
            EventBus.off(EventNames.NOTE_MODAL, this.onNoteModal);
            EventBus.off(EventNames.REMOVE_NOTE, this.onNoteDelete);
            EventBus.off(EventNames.UPDATE_NOTE_TAGS, this.updateUserTags);
        }

    }
</script>
<style scoped lang="scss">
    .v-dialog__content {
        max-height: 100vh;
    }

    .inner-modal-content {
        max-height: calc(90vh - 100px); /* Adjust the remaining height */
        overflow: auto;
    }
</style>
